import axios from 'axios';
import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { baseAxiosConfig } from '../utils/apiConfig';

const userAxios = axios.create(baseAxiosConfig);

/**
 * Get user profile with all details
 * Returns profile data including stats, activity, businesses, favorites
 */
export async function getUserProfile() {
  console.log('🔍 userService - getUserProfile() called');
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    console.log('🔍 userService - Current user:', user?.email);

    if (!user) {
      console.error('🔍 userService - No user found!');
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    console.log('🔍 userService - Getting ID token...');
    const token = await user.getIdToken();
    console.log('🔍 userService - Token received (length):', token?.length);

    const params = {
      user: user.email
    };

    console.log('🔍 userService - Making API call to /user/profile with params:', params);
    const response = await userAxios.get('/user/profile', {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('🔍 userService - API response received:', response.data);
    console.log('🔍 userService - response.data type:', typeof response.data);
    console.log('🔍 userService - response.data.email:', response.data?.email);
    logger.info('User profile fetched successfully');

    const profileData = response.data;
    console.log('🔍 userService - About to return:', profileData);
    return profileData;
  } catch (error) {
    console.error('🔍 userService - ERROR:', error);
    logger.error('Error fetching user profile:', error);
    throw error;
  }
}

/**
 * Update user profile information
 * @param {Object} profileData - Profile data to update
 * @param {string} profileData.display_name - Display name
 * @param {string} profileData.first_name - First name
 * @param {string} profileData.last_name - Last name
 */
export async function updateUserProfile(profileData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    const response = await userAxios.put('/user/profile', profileData, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    logger.info('User profile updated successfully');
    return response.data;
  } catch (error) {
    logger.error('Error updating user profile:', error);
    throw error;
  }
}

/**
 * Get personalized welcome message
 */
export async function getWelcomeMessage() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    const response = await userAxios.get('/user/welcome', {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    logger.info('Welcome message fetched successfully');
    return response.data;
  } catch (error) {
    logger.error('Error fetching welcome message:', error);
    throw error;
  }
}

/**
 * Get user's favorite businesses
 */
export async function getFavoriteBusinesses() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    const response = await userAxios.get('/user/favorites', {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    logger.info('Favorite businesses fetched successfully');
    return response.data;
  } catch (error) {
    logger.error('Error fetching favorites:', error);
    throw error;
  }
}

/**
 * Add a business to favorites
 * @param {string} mapsUri - Google Maps URI of the business
 */
export async function addFavoriteBusiness(mapsUri) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    const response = await userAxios.post('/user/favorites',
      { maps_uri: mapsUri },
      {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    logger.info('Business added to favorites');
    return response.data;
  } catch (error) {
    logger.error('Error adding favorite business:', error);
    throw error;
  }
}

/**
 * Remove a business from favorites
 * @param {string} mapsUri - Google Maps URI of the business
 */
export async function removeFavoriteBusiness(mapsUri) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    // Encode mapsUri for URL
    const encodedUri = encodeURIComponent(mapsUri);

    const response = await userAxios.delete(`/user/favorites/${encodedUri}`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    logger.info('Business removed from favorites');
    return response.data;
  } catch (error) {
    logger.error('Error removing favorite business:', error);
    throw error;
  }
}

/**
 * Check if a business is favorited
 * @param {string} mapsUri - Google Maps URI of the business
 */
export async function checkFavorite(mapsUri) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    // Encode mapsUri for URL
    const encodedUri = encodeURIComponent(mapsUri);

    const response = await userAxios.get(`/user/favorites/check/${encodedUri}`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data.is_favorited;
  } catch (error) {
    logger.error('Error checking favorite status:', error);
    return false;
  }
}

/**
 * Get user's assigned businesses
 */
export async function getUserBusinesses() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    const response = await userAxios.get('/user/businesses', {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    logger.info('User businesses fetched successfully');
    return response.data;
  } catch (error) {
    logger.error('Error fetching user businesses:', error);
    throw error;
  }
}

/**
 * Track user login event
 * Updates login streak and activity tracking
 */
export async function trackLogin() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found for login tracking');
      return null;
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    const response = await userAxios.post('/user/activity/login', {}, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    logger.info('Login tracked successfully:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Error tracking login:', error);
    // Don't throw - login tracking is non-critical
    // We don't want to break the login flow if tracking fails
    return null;
  }
}

/**
 * Track dashboard view event
 * Updates dashboard view counts and last active timestamp
 */
export async function trackDashboardView(dashboardName, metadata = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found for dashboard tracking');
      return null;
    }

    const token = await user.getIdToken();

    const response = await userAxios.post('/user/activity/dashboard-view',
      { dashboard: dashboardName, metadata },
      {
        params: { user: user.email },
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );

    logger.info('Dashboard view tracked:', dashboardName);
    return response.data;
  } catch (error) {
    logger.error('Error tracking dashboard view:', error);
    // Don't throw - tracking is non-critical
    return null;
  }
}

/**
 * Get agency branding settings (logo, agency name)
 */
export async function getAgencyBranding() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      return null;
    }

    const token = await user.getIdToken();

    const response = await userAxios.get('/user/agency-branding', {
      params: { user: user.email },
      headers: { 'Authorization': `Bearer ${token}` }
    });

    return response.data?.branding || null;
  } catch (error) {
    logger.error('Error fetching agency branding:', error);
    return null;
  }
}

/**
 * Update agency branding settings
 * @param {Object} brandingData - Branding data to update
 * @param {string} brandingData.agency_name - Custom agency name
 * @param {string} brandingData.logo_url - Base64 data URL of the logo
 */
export async function updateAgencyBranding(brandingData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const response = await userAxios.put('/user/agency-branding', brandingData, {
      params: { user: user.email },
      headers: { 'Authorization': `Bearer ${token}` }
    });

    logger.info('Agency branding updated successfully');
    return response.data;
  } catch (error) {
    logger.error('Error updating agency branding:', error);
    throw error;
  }
}

/**
 * Send heartbeat to track active time
 * Called periodically by the activity tracker
 * @param {number} minutes - Number of active minutes to report
 */
export async function sendHeartbeat(minutes) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || minutes <= 0) {
      return null;
    }

    const token = await user.getIdToken();

    const response = await userAxios.post('/user/activity/heartbeat',
      { minutes },
      {
        params: { user: user.email },
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );

    logger.info('Heartbeat sent:', minutes, 'minutes');
    return response.data;
  } catch (error) {
    logger.error('Error sending heartbeat:', error);
    // Don't throw - tracking is non-critical
    return null;
  }
}
